import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { ORG } from "../lib/constants";
import { Mail, Phone, MapPin, Clock, Facebook, ArrowRight, CheckCircle2 } from "lucide-react";
import { submitContact } from "../lib/api";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-20 pb-16 md:pt-28 md:pb-20 bg-gradient-to-br from-brand-cream via-brand-bone/30 to-brand-cream">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Contact</div>
              <h1 className="mt-3 font-serif text-5xl md:text-6xl lg:text-7xl text-brand-charcoal tracking-tight leading-[1.02]">
                Come see us in person.
              </h1>
              <p className="mt-6 text-lg text-brand-muted leading-relaxed">
                We'd love to answer your questions, tour the facility with you, or talk through our program. Reach
                out any way that's easiest.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          {/* Info card */}
          <Reveal className="md:col-span-5">
            <div className="bg-white rounded-3xl border border-brand-line p-8 md:p-10 shadow-sm">
              <h2 className="font-serif text-2xl text-brand-charcoal">Hosea Ministries Foundation</h2>
              <div className="mt-6 space-y-5 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-brand-teal mt-0.5" />
                  <div>
                    <div className="font-medium text-brand-charcoal">Visit</div>
                    <div className="text-brand-muted">{ORG.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-brand-teal mt-0.5" />
                  <div>
                    <div className="font-medium text-brand-charcoal">Call or text</div>
                    <a className="text-brand-muted hover:text-brand-teal" href={`tel:${ORG.phoneTel}`}>
                      {ORG.phone} · Nikki Hendricks, Founder
                    </a>
                    <div>
                      <a className="text-brand-muted hover:text-brand-teal" href={`tel:${ORG.secondaryContact.phoneTel}`}>
                        {ORG.secondaryContact.phone} · {ORG.secondaryContact.name}
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-brand-teal mt-0.5" />
                  <div>
                    <div className="font-medium text-brand-charcoal">Email</div>
                    <a className="text-brand-muted hover:text-brand-teal break-all" href={`mailto:${ORG.email}`}>{ORG.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-brand-teal mt-0.5" />
                  <div>
                    <div className="font-medium text-brand-charcoal">Hours</div>
                    <div className="text-brand-muted">{ORG.hours}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Facebook className="w-5 h-5 text-brand-teal mt-0.5" />
                  <a href={ORG.facebookUrl} target="_blank" rel="noreferrer" className="text-brand-muted hover:text-brand-teal">
                    Connect on Facebook
                  </a>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-line">
                <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Application</div>
                <p className="mt-2 text-brand-muted">Ready to apply? We've moved the paper form online.</p>
                <a
                  href="/apply"
                  data-testid="contact-apply-link"
                  className="mt-4 inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-5 py-2.5 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5"
                >
                  Begin application <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>

          {/* Form card */}
          <Reveal delay={120} className="md:col-span-7">
            <div className="bg-white rounded-3xl border border-brand-line p-8 md:p-10 shadow-sm">
              {sent ? (
                <div data-testid="contact-success" className="text-center py-10">
                  <CheckCircle2 className="w-12 h-12 text-brand-teal mx-auto" />
                  <h3 className="mt-4 font-serif text-3xl text-brand-charcoal">Message received</h3>
                  <p className="mt-3 text-brand-muted">Thank you — we'll be in touch soon.</p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-6 inline-flex items-center text-brand-teal font-medium link-underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">
                    Drop us a line
                  </div>
                  <h3 className="font-serif text-3xl text-brand-charcoal">How can we help?</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Your name *">
                      <input
                        required value={form.name} onChange={onChange("name")}
                        data-testid="contact-name"
                        className={fieldCls}
                        placeholder="Jane Doe"
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        required type="email" value={form.email} onChange={onChange("email")}
                        data-testid="contact-email"
                        className={fieldCls}
                        placeholder="you@example.com"
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Phone">
                      <input value={form.phone} onChange={onChange("phone")} data-testid="contact-phone"
                        className={fieldCls} placeholder="(555) 555-0000" />
                    </Field>
                    <Field label="Subject">
                      <input value={form.subject} onChange={onChange("subject")} data-testid="contact-subject"
                        className={fieldCls} placeholder="Tour request, donation, etc." />
                    </Field>
                  </div>
                  <Field label="Message *">
                    <textarea
                      required rows={6} value={form.message} onChange={onChange("message")}
                      data-testid="contact-message"
                      className={fieldCls}
                      placeholder="Tell us a little about your question…"
                    />
                  </Field>

                  {/* Honeypot */}
                  <input
                    type="text" tabIndex={-1} autoComplete="off"
                    value={form.website} onChange={onChange("website")}
                    name="website"
                    className="hidden" aria-hidden="true"
                  />

                  <div className="flex items-center justify-between gap-4 pt-2">
                    <p className="text-xs text-brand-muted">We'll never share your information.</p>
                    <button
                      type="submit" disabled={loading}
                      data-testid="contact-submit"
                      className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-6 py-3 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? "Sending…" : (<>Send message <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const fieldCls =
  "w-full bg-brand-cream border border-brand-line rounded-xl px-4 py-3 text-brand-charcoal placeholder-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-teal/25 focus:border-brand-teal transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-brand-muted mb-1.5 tracking-wide">{label}</span>
      {children}
    </label>
  );
}
